import { useEffect, useState } from "react";
import axios from "axios";
import { Crown, Lock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Premium Options card — shown inside the Billing & Design section of
 * Settings. The 5 templates are shipped as code (no upload feature) and
 * gated by the user's `premium_enabled` flag, which only the super-admin
 * can toggle from User Management.
 */
export default function PremiumOptions({ user, current, onPick }) {
  const [templates, setTemplates] = useState([]);
  const locked = !user?.premium_enabled;

  useEffect(() => {
    axios
      .get(`${API}/premium/templates`)
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]));
  }, []);

  return (
    <div
      data-testid="premium-options-card"
      className="relative mb-8 rounded-md overflow-hidden border-2"
      style={{
        borderColor: locked ? "#E2E8F0" : "#D4AF37",
        background: locked
          ? "#FAFAF8"
          : "linear-gradient(135deg, #0B0F1A 0%, #16213E 100%)",
        color: locked ? "#0F172A" : "#FFFFFF",
      }}
    >
      {/* Header band */}
      <div
        className="px-6 py-4 flex items-center justify-between"
        style={{
          background: locked
            ? "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)"
            : "linear-gradient(135deg, #D4AF37 0%, #8B7000 100%)",
        }}
      >
        <div className="flex items-center gap-3">
          <Crown size={28} weight="fill" color={locked ? "#94A3B8" : "#FFFFFF"} />
          <div>
            <h3
              className={`text-lg font-black tracking-wider uppercase ${
                locked ? "text-slate-500" : "text-white"
              }`}
            >
              Premium Options
            </h3>
            <p
              className={`text-xs ${locked ? "text-slate-500" : "text-amber-50"}`}
            >
              5 hand-crafted luxury designs &mdash; one-time setup, no uploads needed
            </p>
          </div>
        </div>
        {locked && (
          <div
            className="flex items-center gap-1 px-3 py-1 bg-white border border-slate-300 rounded-full text-xs font-bold text-slate-600"
            data-testid="premium-locked-badge"
          >
            <Lock size={14} weight="fill" />
            LOCKED
          </div>
        )}
      </div>

      {/* Locked notice */}
      {locked && (
        <div className="px-6 py-4 bg-amber-50 border-b border-amber-200">
          <p className="text-sm text-amber-900">
            <strong>Premium access required.</strong> Ask your super-admin to enable Premium Templates for you from{" "}
            <span className="font-mono text-xs bg-amber-100 px-2 py-0.5 rounded">User Management</span>.
          </p>
        </div>
      )}

      {/* Template grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
        {templates.map((t) => {
          const selected = current === t.key && !locked;
          return (
            <button
              key={t.key}
              type="button"
              disabled={locked}
              onClick={() => onPick(t.key)}
              data-testid={`premium-template-${t.key}`}
              className={`text-left rounded-md overflow-hidden border-2 transition-all ${
                locked ? "opacity-50 cursor-not-allowed" : "hover:scale-[1.02] cursor-pointer"
              } ${
                selected
                  ? "border-amber-400 shadow-lg shadow-amber-400/40"
                  : locked
                  ? "border-slate-200"
                  : "border-slate-700 hover:border-amber-300"
              }`}
            >
              {/* Preview swatch */}
              <div
                className="h-28 relative flex items-end p-3"
                style={{
                  background:
                    t.key === "aurora-elite"
                      ? "linear-gradient(135deg,#0B1020 0%,#1A2240 60%,#D4AF37 100%)"
                      : t.key === "global-traders"
                      ? "linear-gradient(135deg,#FFFFFF 0%,#FFE5C2 60%,#F97316 100%)"
                      : t.key === "quantum-solutions"
                      ? "linear-gradient(135deg,#FFFFFF 0%,#CCFBF1 60%,#0F766E 100%)"
                      : t.key === "nexus-dynamics"
                      ? "linear-gradient(135deg,#0A1024 0%,#10172E 50%,#22D3EE 100%)"
                      : "linear-gradient(135deg,#0E0E12 0%,#15171D 60%,#C0C0C0 100%)",
                }}
              >
                {selected && (
                  <span className="absolute top-2 right-2 bg-amber-400 text-amber-950 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                    Selected
                  </span>
                )}
                <Crown
                  size={20}
                  weight="fill"
                  className="absolute top-2 left-2"
                  color={t.preview_color}
                />
              </div>
              <div className={`px-3 py-2 ${locked ? "bg-white" : "bg-slate-900"}`}>
                <p className={`text-sm font-bold ${locked ? "text-slate-700" : "text-white"}`}>
                  {t.name}
                </p>
                <p className={`text-[11px] ${locked ? "text-slate-500" : "text-slate-300"}`}>
                  {t.tagline}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
